import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  MailOutline as MailIcon,
} from '@mui/icons-material';
import api from '../config/api';
import {
  DOCUMENT,
  documentTextButtonSx,
} from '../constants/documentTheme';

/**
 * Booking-email forward tip.
 * - default: collapsible card on trip lists
 * - context="tripDetail": clearer “don’t type bookings by hand” copy on trip pages
 */
const EmailForwardingCard = ({
  compact = false,
  variant = 'traveler',
  context = 'list',
  defaultExpanded = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isPartner = variant === 'partner';
  const isTripDetail = context === 'tripDetail';
  const [address, setAddress] = useState('plans@in.dejavunomad.com');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(Boolean(defaultExpanded || isTripDetail));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/inbound-email/address', {
          usePartnerAuth: isPartner,
        });
        if (!cancelled && res.data?.address) setAddress(res.data.address);
      } catch (_) {
        // keep default address
      }
    })();
    return () => { cancelled = true; };
  }, [isPartner]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      setError(t('emailForward.copyFailed'));
    }
  }, [address, t]);

  const summary = isTripDetail
    ? t(isPartner ? 'emailForward.tripDetailPartnerSummary' : 'emailForward.tripDetailSummary')
    : t(isPartner ? 'emailForward.partnerCollapseSummary' : 'emailForward.collapseSummary');

  const body = isTripDetail
    ? t(isPartner ? 'emailForward.tripDetailPartnerBody' : 'emailForward.tripDetailBody')
    : t(isPartner ? 'emailForward.partnerCardBody' : 'emailForward.cardBody');

  return (
    <Box
      data-testid="email-forwarding-card"
      data-context={context}
      sx={{
        mb: 2,
        bgcolor: DOCUMENT.surface,
        border: `1px solid ${DOCUMENT.ruleHard}`,
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        data-testid="email-forwarding-toggle"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          p: compact ? 1.5 : 2,
          m: 0,
          border: 0,
          bgcolor: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          font: 'inherit',
          color: 'inherit',
          '&:hover': { bgcolor: DOCUMENT.ivory },
        }}
      >
        <MailIcon sx={{ fontSize: 18, color: DOCUMENT.oxblood, flexShrink: 0 }} />
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: DOCUMENT.display,
            fontSize: compact ? 16 : 18,
            color: DOCUMENT.ink,
            lineHeight: 1.3,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          {summary}
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 22,
            color: DOCUMENT.ink3,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            flexShrink: 0,
          }}
        />
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: compact ? 1.5 : 2, pb: compact ? 1.5 : 2, pt: 0 }}>
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mb: 1.5, lineHeight: 1.55 }}>
            {body}
          </Typography>
          <Typography
            component="pre"
            sx={{
              fontSize: 12,
              color: DOCUMENT.ink3,
              mb: 1.5,
              lineHeight: 1.5,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              whiteSpace: 'pre-wrap',
              m: 0,
            }}
          >
            {t(isPartner ? 'emailForward.partnerTripForTip' : 'emailForward.tripForTip')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              component="code"
              sx={{
                fontSize: 13,
                px: 1.25,
                py: 0.75,
                bgcolor: DOCUMENT.ivory,
                border: `1px solid ${DOCUMENT.rule}`,
                color: DOCUMENT.ink,
              }}
            >
              {address}
            </Typography>
            <IconButton
              size="small"
              aria-label={t('emailForward.copyAddress')}
              onClick={handleCopy}
              data-testid="copy-forwarding-address"
            >
              <CopyIcon sx={{ fontSize: 18 }} />
            </IconButton>
            {copied && (
              <Typography sx={{ fontSize: 12, color: DOCUMENT.accent }}>{t('emailForward.copied')}</Typography>
            )}
            <Button
              onClick={() => navigate(isPartner ? '/partner/email-imports' : '/email-imports')}
              sx={{ ...documentTextButtonSx, ml: { sm: 'auto' } }}
            >
              {t('emailForward.viewImports')}
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError('')}>{error}</Alert>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default EmailForwardingCard;
